import FormLabel from '@c/forms/controls/FormLabel';
import FormTextarea from '@c/forms/controls/FormTextarea';
import { ImageList } from '@c/listing/components/ImageList';
import { AddPhotoIcon } from '@c/listing/icons/AddPhotoIcon';
import LoadingSpinner from '@c/LoadingSpinner';
import BaseModal from '@c/modals/BaseModal';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import RadioButton from '@ui/RadioButton';
import useMultiImageUpload from '@util/hooks/useMultiImageUpload';
import { useReturns } from 'app/support/hooks/useReturns';
import { useToastContext } from 'context/ToastContext';
import { ReturnDocument } from 'models/returns';
import { useState } from 'react';

export const MarkReturnAsReceivedModal = ({
  isOpen,
  dismiss,
  returnDoc,
}: {
  isOpen: boolean;
  dismiss: () => void;
  returnDoc: ReturnDocument;
}) => {
  const [receivedForm, setReceivedForm] = useState({
    received_by_warehouse: returnDoc.received_by_warehouse ?? 'yes',
    warehouse_comments: returnDoc.warehouse_comments ?? '',
    images: returnDoc.images ?? [],
  });
  const { showSuccessToast, showErrorToast } = useToastContext();
  const { markAsReceivedMutation } = useReturns({
    onMarkedAsReceived: (success) => {
      if (success) {
        dismiss();
        if (receivedForm.received_by_warehouse === 'needs_attention') {
          showSuccessToast(
            'Return issue reported. Our support team will contact you shortly'
          );
        } else {
          showSuccessToast('Return marked as received');
        }
        returnDoc.received_by_warehouse = 'yes';
      } else {
        showErrorToast('Failed to mark return as received');
      }
    },
  });
  const { uploadingImage, handleFileInputChange, deleteImage } =
    useMultiImageUpload({
      id: `${returnDoc.id}_${returnDoc.seller_id}`,
      type: 'other',
      onUploadCallback: ({ paths }) => {
        setReceivedForm({
          ...receivedForm,
          images: [...(receivedForm.images ?? []), ...paths],
        });
      },
      onDeleteCallback: (paths) => {
        setReceivedForm({ ...receivedForm, images: paths });
      },
    });
  return (
    <BaseModal isOpen={isOpen} dismiss={dismiss} title="Mark as Received">
      <div className="flex flex-col gap-4 p-8">
        {receivedForm.received_by_warehouse === 'needs_attention' && (
          <FormLabel value="Comments" className="text-[2rem]">
            <p className="my-4 text-[1.6rem] text-gray-500">
              If the item arrived in its original condition, please mark as
              received. If there are any issues, describe them below.
            </p>
            <FormTextarea
              value={receivedForm.warehouse_comments}
              onChange={(e) =>
                setReceivedForm({
                  ...receivedForm,
                  warehouse_comments: e.target.value,
                })
              }
              placeholder="Ex: Item came back damaged — scratches on visor and missing original packaging"
            />
          </FormLabel>
        )}

        {receivedForm.received_by_warehouse === 'needs_attention' && (
          <div className="flex w-full grow flex-col gap-4 px-4 ">
            <span className="font-semibold">Photos</span>
            <p className="text-[1.6rem] text-gray-500">
              Add supporting photos if the item arrived damaged or not in its
              original condition.
            </p>
            <button
              type="button"
              className={
                'h-[15rem] w-full cursor-pointer overflow-clip rounded-[1.6rem] outline-dashed outline-2 outline-brand-secondary-light hover:bg-brand-darker-white  focus:outline-brand-secondary'
              }
            >
              <label
                htmlFor="addPhotoInput"
                className="flex h-full w-full cursor-pointer flex-col  items-center justify-center gap-[1.3rem]"
              >
                {uploadingImage ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <AddPhotoIcon />
                    <p className="hidden text-[1.3rem] font-semibold text-brand-gray sm:block">
                      Click or drop your photos here
                    </p>
                  </>
                )}
              </label>
              <input
                id="addPhotoInput"
                type="file"
                accept="image/*,video/*"
                multiple
                name="photo"
                className="hidden"
                onChange={handleFileInputChange}
              />
            </button>
            <ImageList images={receivedForm.images} deleteImage={deleteImage} />
          </div>
        )}
        <RadioButton
          label="Item arrived in good condition"
          checked={receivedForm.received_by_warehouse === 'yes'}
          onClick={() => {
            setReceivedForm({
              ...receivedForm,
              received_by_warehouse: 'yes',
            });
          }}
        />
        <RadioButton
          label="Item arrived damaged or not in original condition"
          checked={receivedForm.received_by_warehouse === 'needs_attention'}
          onClick={() => {
            setReceivedForm({
              ...receivedForm,
              received_by_warehouse: 'needs_attention',
            });
          }}
        />

        <Button
          text={
            receivedForm.received_by_warehouse === 'needs_attention'
              ? 'Report Item Issue'
              : 'Mark as Received'
          }
          loading={markAsReceivedMutation.isPending}
          type="secondary"
          onClick={() => {
            if (
              receivedForm.received_by_warehouse === 'needs_attention' &&
              !receivedForm.warehouse_comments
            ) {
              showErrorToast(
                'Please provide details about the condition of the item'
              );
              return;
            }
            markAsReceivedMutation.mutate({
              id: returnDoc.id,
              received_by_warehouse: receivedForm.received_by_warehouse,
              warehouse_comments: receivedForm.warehouse_comments,
              images: receivedForm.images,
              is_warehouse_return: returnDoc.is_warehouse_return,
              order_id: returnDoc.order_id,
            });
          }}
        />
      </div>
    </BaseModal>
  );
};
